import React, { useRef, useState } from "react";
import { UploadCloud, X, ImageIcon, Camera } from "lucide-react";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";

const UploadDropzone = ({ file, previewUrl, onFile, onClear }) => {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = (files) => {
    if (files && files[0]) onFile(files[0]);
  };

  if (file && previewUrl) {
    return (
      <div data-testid="uploaded-preview" className="relative border-2 border-border rounded-md overflow-hidden bg-secondary/40">
        <div className="aspect-[4/3] flex items-center justify-center p-4">
          <img src={previewUrl} alt="uploaded part" className="max-h-full max-w-full object-contain" />
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t bg-background">
          <span className="text-xs font-mono truncate max-w-[70%]">{file.name}</span>
          <button
            data-testid="clear-upload-btn"
            onClick={onClear}
            className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground hover:text-brand transition-colors"
          >
            <X className="w-4 h-4" /> Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="upload-dropzone"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={`cursor-pointer border-2 border-dashed rounded-md transition-colors p-12 sm:p-16 flex flex-col items-center justify-center text-center ${
        drag ? "border-brand bg-brand/5" : "border-border hover:border-foreground/40 bg-secondary/30"
      }`}
    >
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-5 transition-colors ${drag ? "bg-brand text-white" : "bg-background border"}`}>
        {drag ? <ImageIcon className="w-7 h-7" /> : <UploadCloud className="w-7 h-7" strokeWidth={1.8} />}
      </div>
      <p className="font-heading font-bold text-lg">Drag &amp; drop part image here</p>
      <p className="text-sm text-muted-foreground mt-1">or click to choose a file</p>

      <div className="flex items-center gap-3 mt-5" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          data-testid="choose-file-btn"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider border rounded-sm px-4 py-2 bg-background hover:border-brand transition-colors"
        >
          <UploadCloud className="w-4 h-4" /> Choose file
        </button>
        <button
          type="button"
          data-testid="take-photo-btn"
          onClick={() => cameraRef.current?.click()}
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider border rounded-sm px-4 py-2 bg-background hover:border-brand transition-colors"
        >
          <Camera className="w-4 h-4" /> Take photo
        </button>
      </div>

      <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-5">JPG · JPEG · PNG · WEBP</p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid="file-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="camera-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};

export default UploadDropzone;
